import { useMemo } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import { ArrowLeft } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { usePurchaseOrders, useSupplier } from "~/hooks/purchasing";
import { formatCurrency, formatDate, formatRut } from "~/utils/helpers";

import { PurchaseOrdersTable } from "./components/PurchaseOrdersTable";

import type { PurchaseOrder } from "~/types/purchasing";

const PENDING_STATUSES: PurchaseOrder["status"][] = ["draft", "sent", "partial"];

export function ComprasProveedorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: supplier, isLoading: loadingSupplier } = useSupplier(id);
  const { data: orders = [], isLoading: loadingOrders } = usePurchaseOrders({
    supplier_id: id,
  });

  const kpis = useMemo(() => {
    const count = orders.length;
    const pending = orders.filter((o) =>
      PENDING_STATUSES.includes(o.status)
    ).length;
    const totalClp = orders
      .filter((o) => o.currency === "CLP")
      .reduce((sum, o) => sum + o.total, 0);
    const lastDate = orders.reduce<string | null>(
      (max, o) => (max === null || o.issue_date > max ? o.issue_date : max),
      null
    );
    return { count, pending, totalClp, lastDate };
  }, [orders]);

  const backButton = (
    <Button asChild variant="ghost" size="sm" className="-ml-2">
      <Link to="/purchasing/compras/proveedores">
        <ArrowLeft className="mr-1 h-4 w-4" />
        Volver
      </Link>
    </Button>
  );

  if (loadingSupplier) {
    return (
      <PageContainer>
        <Skeleton className="mb-6 h-9 w-64" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (!supplier) {
    return (
      <PageContainer>
        <PageHeader title="Proveedor no encontrado" back={backButton} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={supplier.name}
        description={supplier.rut ? formatRut(supplier.rut) : undefined}
        back={backButton}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="bg-gray-100 text-gray-800">
              {supplier.is_foreign ? "Extranjero" : "Nacional"}
            </Badge>
            <Badge
              variant={supplier.is_active ? "default" : "secondary"}
              className={
                supplier.is_active
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }
            >
              {supplier.is_active ? "Activo" : "Inactivo"}
            </Badge>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Órdenes de compra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{kpis.count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pendientes de recepción
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{kpis.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Monto total (CLP)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">${formatCurrency(kpis.totalClp)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Última OC
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {kpis.lastDate ? formatDate(kpis.lastDate) : "-"}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">
            Órdenes de compra
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <PurchaseOrdersTable
            orders={orders}
            isLoading={loadingOrders}
            onRowClick={(order) => navigate(`/purchasing/orders/${order.id}`)}
            readOnly
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
}
